/**
 * Phase 8 · Wrangle parity — Copilot summary card.
 *
 * Spec: docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md §33 (image 1, annotation 3).
 *
 * Pinned ABOVE the agent conversation in the left column of the Smart Match
 * and Sourcing canvases. Summarises the current candidate pool so the user
 * doesn't have to scroll through every reply to get the shape of the result:
 *
 *     200 total candidates evaluated
 *     128 Excellent matches 90%+
 *      50 High matches 80%–89%
 *      18 Medium matches 60%–79%
 *       4 Low matches <60%
 *      90% Average score
 *     100% Top score
 *
 *     The majority of candidates are highly qualified, with over 89% scoring
 *     80% or higher. Consider reviewing top-tier candidates for immediate
 *     advancement or refining criteria to explore more diverse options.
 *
 * Renders nothing when `matches` is empty — the card appears on first result
 * and stays in sync with bucket changes (shortlist / hide) because the parent
 * re-renders with the filtered list.
 *
 * Stats-card, not a chat bubble: the card is *static* computed output from
 * the match list, not something the LLM paraphrases. This keeps the stats
 * honest (no drift between what the card says and what the list contains)
 * and removes a round-trip through the LLM on every result update.
 */

import { useCallback, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { MatchResult } from '../types';

const LS_COPILOT_OPEN = 'ggj.copilot.summary-open';

function readOpen(): boolean {
  try {
    return localStorage.getItem(LS_COPILOT_OPEN) === '1';
  } catch {
    return false;
  }
}

function writeOpen(open: boolean): void {
  try {
    localStorage.setItem(LS_COPILOT_OPEN, open ? '1' : '0');
  } catch {
    /* ignore */
  }
}

interface CopilotSummaryCardProps {
  /** Matches surfaced for the current tab/search. */
  matches: MatchResult[];
  /** Optional: override the heading. Defaults to "Copilot". */
  heading?: string;
}

interface Bins {
  total: number;
  excellent: number;
  high: number;
  medium: number;
  low: number;
  avg: number;
  top: number;
}

function binScores(matches: MatchResult[]): Bins {
  const total = matches.length;
  if (total === 0) {
    return { total: 0, excellent: 0, high: 0, medium: 0, low: 0, avg: 0, top: 0 };
  }

  let excellent = 0;
  let high = 0;
  let medium = 0;
  let low = 0;
  let sum = 0;
  let top = 0;

  for (const m of matches) {
    // Defensive: composite_score is nominally 0–100 but we clamp so a
    // stray 0–1 fraction or an out-of-range LLM score doesn't wreck the
    // averages. The rest of the app already does `Math.round(score)`
    // against this field (see ScoreChip) without normalising.
    const raw = typeof m.composite_score === 'number' ? m.composite_score : 0;
    const score = raw <= 1 ? raw * 100 : raw;
    const clamped = Math.max(0, Math.min(100, score));

    if (clamped >= 90) excellent += 1;
    else if (clamped >= 80) high += 1;
    else if (clamped >= 60) medium += 1;
    else low += 1;

    sum += clamped;
    if (clamped > top) top = clamped;
  }

  return {
    total,
    excellent,
    high,
    medium,
    low,
    avg: Math.round(sum / total),
    top: Math.round(top),
  };
}

/**
 * Narrative sentence that mirrors the Wrangle card — keyed off the dominant
 * bin rather than the raw average because "avg = 70" can mean two very
 * different pools (all 70s vs half 90s + half 50s). We pick the message
 * from the loudest bucket so the CTA matches the data.
 */
function narrativeFor(bins: Bins): string {
  if (bins.total === 0) return '';

  const strongShare = (bins.excellent + bins.high) / bins.total;
  const weakShare = bins.low / bins.total;

  if (strongShare >= 0.6) {
    return 'The majority of candidates are highly qualified — over 80% fall in the 80%+ bands. Consider reviewing top-tier candidates for immediate advancement or refining criteria to explore more diverse options.';
  }
  if (bins.excellent >= 5) {
    return `${bins.excellent} candidates score 90%+ and are ready for immediate outreach. Broaden the middle band if you need more options, or narrow the must-haves to keep the top tier only.`;
  }
  if (weakShare >= 0.5) {
    return 'Most candidates are below the 60% fit bar — the search is likely too broad, or the must-have list is rare in the pool. Try loosening one constraint at a time to see where the cliff is.';
  }
  if (bins.medium >= bins.high && bins.medium > 0) {
    return 'The pool skews toward medium fits (60%–79%). Tightening the must-have list or raising the seniority filter typically lifts the top-band count.';
  }
  return 'Balanced pool. Review the top bands first, then scan the medium band for candidates who match on domain but may need upskilling.';
}

export function CopilotSummaryCard({ matches, heading = 'Copilot' }: CopilotSummaryCardProps) {
  const bins = useMemo(() => binScores(matches), [matches]);
  const narrative = useMemo(() => narrativeFor(bins), [bins]);
  // Post user-feedback: default collapsed so the chat + composer below
  // actually fit on screen. Clicking the header toggles (persists).
  const [open, setOpen] = useState<boolean>(() => readOpen());
  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      writeOpen(next);
      return next;
    });
  }, []);

  if (bins.total === 0) return null;

  // Condensed one-liner for the collapsed state. Surfaces the two numbers
  // that matter most at a glance: total evaluated + top score.
  const collapsedSummary = `${bins.total} candidate${
    bins.total === 1 ? '' : 's'
  } · top ${bins.top}%`;

  return (
    <section
      aria-label="Copilot summary"
      className="rounded-[10px] border"
      style={{
        background: 'var(--vscode-bg-elevated)',
        borderColor: 'var(--vscode-border-primary)',
      }}
    >
      <button
        type="button"
        onClick={toggle}
        className="flex w-full items-center gap-2 rounded-[10px] px-3 py-2 text-left transition-colors hover:bg-[var(--vscode-bg-hover)]"
        aria-expanded={open}
        aria-controls="ggj-copilot-summary-body"
      >
        {open ? (
          <ChevronDown
            className="h-3.5 w-3.5 shrink-0"
            style={{ color: 'var(--vscode-text-tertiary)' }}
            aria-hidden
          />
        ) : (
          <ChevronRight
            className="h-3.5 w-3.5 shrink-0"
            style={{ color: 'var(--vscode-text-tertiary)' }}
            aria-hidden
          />
        )}
        <span
          className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold"
          aria-hidden
          style={{
            background: 'var(--ggj-accent-soft, #D1FAE5)',
            color: '#065F46',
          }}
        >
          ✦
        </span>
        <h3
          className="text-[13px] font-semibold"
          style={{ color: 'var(--vscode-text-primary)' }}
        >
          {heading}
        </h3>
        <span
          className="truncate text-[11px]"
          style={{ color: 'var(--vscode-text-tertiary)' }}
        >
          · {collapsedSummary}
        </span>
      </button>

      {open ? (
        <div id="ggj-copilot-summary-body" className="border-t px-4 pb-4 pt-3"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
        >
          <p
            className="mb-3 text-[12px] leading-relaxed"
            style={{ color: 'var(--vscode-text-secondary)' }}
          >
            The candidate pool shows{' '}
            {bins.excellent + bins.high >= bins.medium + bins.low
              ? 'a strong alignment'
              : 'partial alignment'}{' '}
            with the search criteria.
          </p>

          <ul className="mb-3 space-y-1.5 text-[12px]">
            <Stat label="total candidates evaluated" value={bins.total} />
            <Stat label="Excellent matches 90%+" value={bins.excellent} emphasis />
            <Stat label="High matches 80%–89%" value={bins.high} />
            <Stat label="Medium matches 60%–79%" value={bins.medium} />
            <Stat label="Low matches <60%" value={bins.low} />
            <Stat label="Average score" value={`${bins.avg}%`} />
            <Stat label="Top score" value={`${bins.top}%`} emphasis />
          </ul>

          {narrative ? (
            <p
              className="text-[12px] leading-relaxed"
              style={{ color: 'var(--vscode-text-secondary)' }}
            >
              {narrative}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function Stat({
  label,
  value,
  emphasis = false,
}: {
  label: string;
  value: number | string;
  emphasis?: boolean;
}) {
  return (
    <li className="flex items-baseline gap-2">
      <span
        className={emphasis ? 'font-semibold' : 'font-medium'}
        style={{
          color: emphasis ? 'var(--ggj-accent, #10B981)' : 'var(--vscode-text-primary)',
          minWidth: 48,
          display: 'inline-block',
        }}
      >
        {value}
      </span>
      <span style={{ color: 'var(--vscode-text-secondary)' }}>{label}</span>
    </li>
  );
}
